import React, { useState } from 'react';
import RestrictedAccess from '../RestrictedAccess/RestrictedAccess';
import AIReportModal from '../AIReportModal/AIReportModal';
import ProjectAIReportModal from '../ProjectAIReportModal/ProjectAIReportModal';
import { useAuth } from '../../context/AuthContext';
import {
  fetchAIReport,
  fetchDirectAwardAIReport,
  fetchProjectAIReport,
} from '../../services/aiReportsApi';
import {
  checkOfferGenerationRights,
  consumeCredit,
} from '../../services/creditsService';

const PremiumAIReport = ({
  noticeNo,
  type = 'auction',
  buttonLabel = 'Genereaza Raport AI',
  modalTitle = 'Raport AI Premium',
  projectTitle,
}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [aiReport, setAiReport] = useState(null);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [showAIReport, setShowAIReport] = useState(false);
  const [error, setError] = useState(null);

  const handleDownloadAIReport = async () => {
    if (authLoading) return;

    setAiReportLoading(true);
    setError(null);

    // Check localStorage cache first (no credit needed for cached reports)
    const local = window.localStorage.getItem(`aiReportV2_${noticeNo}`);
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (parsed && typeof parsed === 'object' && (parsed.project_title || parsed.project_overview)) {
          setAiReport(parsed);
          setShowAIReport(true);
          setAiReportLoading(false);
          return;
        }
      } catch {}
    }

    // Check credits before generating (only for new reports, not cached)
    try {
      const creditsCheck = await checkOfferGenerationRights();

      if (!creditsCheck.canGenerate) {
        setError(
          `Credite insuficiente pentru generarea raportului AI. Disponibile: ${creditsCheck.totalCredits || 0}. Cumpara credite extra.`,
        );
        setAiReportLoading(false);
        return;
      }
    } catch {
      setError('Nu s-au putut verifica creditele. Incearca din nou.');
      setAiReportLoading(false);
      return;
    }

    // Generate report
    try {
      let data;
      if (type === 'auction') {
        data = await fetchAIReport(noticeNo);
      } else if (type === 'directAward') {
        data = await fetchDirectAwardAIReport(noticeNo);
      } else if (type === 'project') {
        data = await fetchProjectAIReport(noticeNo);
      } else {
        throw new Error('Tip necunoscut pentru raport AI');
      }

      if (data && (data.project_title || data.project_overview)) {
        // Consume credit after successful generation
        const creditResult = await consumeCredit('AI_REPORT_GENERATION', noticeNo);
        if (!creditResult.success) {
          console.warn('Credit consumption failed:', creditResult.message);
        }

        setAiReport(data);
        window.localStorage.setItem(
          `aiReportV2_${noticeNo}`,
          JSON.stringify(data),
        );
        setShowAIReport(true);

        // Refresh credits in header after consumption
        window.dispatchEvent(new CustomEvent('creditsUpdated'));
      } else {
        setError(
          'Raportul AI nu a putut fi generat pentru aceasta procedura. Incearca din nou.',
        );
      }
    } catch {
      setError(
        'A aparut o eroare la generarea raportului AI. Incearca din nou mai tarziu.',
      );
    } finally {
      setAiReportLoading(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-to-br from-yellow-50 via-white to-indigo-50 border border-yellow-200 rounded-xl shadow-lg p-4 flex flex-col justify-between gap-3 h-full">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="text-yellow-500"
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-yellow-700 leading-tight">
                {modalTitle}
              </h2>
              <div className="flex items-center gap-1.5">
                <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[9px] font-bold border border-green-200 tracking-wider">
                  BETA
                </span>
                <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600 text-[9px] font-bold border border-indigo-200 tracking-wider">
                  1 CREDIT
                </span>
              </div>
            </div>
          </div>
          <p className="text-gray-600 text-xs leading-snug mb-2">
            {type === 'project'
              ? 'Analiza completa a proiectului: pipeline, competitie, oportunitate, riscuri, recomandari strategice.'
              : 'Sinteza automata cu analiza BIM, CAPEX/OPEX, riscuri, WBS, Uniclass, certificari si multe altele.'}
          </p>
          <ul className="space-y-1 mb-3">
            {(type === 'project' ? [
              'Pipeline & model executie',
              'Analiza competitiva & castigatori',
              'Evaluare oportunitate & checklist',
              'Riscuri & recomandari strategice',
            ] : [
              'Analiza BIM & standarde',
              'Clasificare CAPEX/OPEX',
              'Riscuri & complexitate',
              'WBS & timeline proiect',
            ]).map((item, idx) => (
              <li
                key={idx}
                className="flex items-center gap-1.5 text-xs text-gray-700"
              >
                <svg
                  className="w-3.5 h-3.5 text-green-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <RestrictedAccess message="Disponibil doar pentru utilizatorii autentificati">
            <button
              onClick={handleDownloadAIReport}
              disabled={aiReportLoading}
              className={`w-full px-4 py-2.5 rounded-xl text-sm font-bold shadow-md transition ${aiReportLoading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-indigo-500 text-white hover:from-yellow-500 hover:to-indigo-600'}`}
            >
              {aiReportLoading ? (
                <span className="flex items-center gap-2 justify-center">
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l5-5-5-5v4A12 12 0 002 12h2z"
                    />
                  </svg>
                  Generare...
                </span>
              ) : aiReport ? (
                'Vezi Raport AI'
              ) : (
                buttonLabel
              )}
            </button>
          </RestrictedAccess>
          {error && (
            <div className="mt-2 w-full bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5">
              <svg
                className="w-4 h-4 text-red-400 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v4m0 4h.01"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}
        </div>
      </section>
      {showAIReport && type === 'project' && (
        <ProjectAIReportModal
          isOpen={true}
          report={aiReport}
          projectTitle={projectTitle}
          onClose={() => {
            setShowAIReport(false);
            setAiReport(null);
          }}
        />
      )}
      {showAIReport && type !== 'project' && (
        <AIReportModal
          report={aiReport}
          loading={aiReportLoading}
          noticeNo={noticeNo}
          onClose={() => {
            setShowAIReport(false);
            setAiReport(null);
          }}
        />
      )}
    </>
  );
};

export default PremiumAIReport;
